import { useState } from "react";

import styles from "./Settings.module.css";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { ApiError } from "../../data/api/client";
import {
  type ApiRepoSecret,
  deleteRepoSecret,
  setRepoSecret,
  useRepoSecrets,
} from "../../data/api/repositories";
import { Icon } from "../../icons";
import { relTime } from "../../lib/time";

// Repository secrets (repositories.md §Secrets).
//
// The environment every sandbox for this repository is started with: an API key
// the app under test needs, a token its integration suite calls out with. The
// value is write-only — it is sealed on the way in and is never readable back
// out, here or anywhere else — so the list can only show what it honestly
// knows: the name, when it changed, and whether a sandbox has ever been handed
// it. "Never used" is the useful half of that: it is how a secret nobody reads,
// or one spelled slightly wrong, gets noticed.

const NAME_RE = /^[A-Z][A-Z0-9_]*$/;

const ms = (iso: string | null): number | null =>
  iso ? Date.parse(iso) : null;

const usage = (secret: ApiRepoSecret): string => {
  const used = ms(secret.last_used_at);
  return used === null
    ? "never used by a sandbox"
    : `last used ${relTime(used)}`;
};

export type RepoSecretsProps = {
  readonly repoId: string;
};

export const RepoSecrets = ({ repoId }: RepoSecretsProps) => {
  const { loading, refetch, secrets } = useRepoSecrets(repoId);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = name.trim().toUpperCase();
  const canAdd = NAME_RE.test(trimmedName) && value.length > 0 && busy === null;
  const replacing = secrets.some((s) => s.name === trimmedName);

  const add = (): void => {
    if (!canAdd) {
      // Say why rather than leaving a disabled button to explain itself.
      if (trimmedName && !NAME_RE.test(trimmedName)) {
        setError("Use an environment-variable name: FOO, FOO_BAR, API_KEY_2.");
      }
      return;
    }
    setBusy("add");
    setError(null);
    void setRepoSecret(repoId, trimmedName, value)
      .then(() => {
        setName("");
        setValue("");
        refetch();
      })
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError ? err.message : "Couldn't save that secret.",
        ),
      )
      .finally(() => setBusy(null));
  };

  const remove = (secretName: string): void => {
    setBusy(`rm:${secretName}`);
    setError(null);
    void deleteRepoSecret(repoId, secretName)
      .then(refetch)
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError
            ? err.message
            : "Couldn't remove that secret.",
        ),
      )
      .finally(() => setBusy(null));
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Secrets</h3>
      <div className={styles.card}>
        <div className={styles.row}>
          <div className={styles.grow}>
            <div className={styles.help}>
              Environment variables for every sandbox this repository starts.
              Values are encrypted and never shown again — a running sandbox
              keeps the value it booted with.
            </div>
          </div>
        </div>

        {secrets.map((secret) => (
          <div className={styles.row} key={secret.name}>
            <span className={styles.secretIcon}>
              <Icon.Shield size={15} />
            </span>
            <div className={styles.grow}>
              <div className={styles.secretName}>{secret.name}</div>
              <div className={styles.help}>
                updated {relTime(Date.parse(secret.updated_at))} ·{" "}
                {usage(secret)}
              </div>
            </div>
            <Button
              disabled={busy !== null}
              onClick={() => remove(secret.name)}
              size="sm"
              variant="ghost"
            >
              Remove
            </Button>
          </div>
        ))}

        {!loading && secrets.length === 0 ? (
          <div className={styles.row}>
            <span className={styles.help}>
              No secrets yet. Sandboxes get only the repository config&apos;s
              own <code>env</code> and the credentials Tinsmith supplies.
            </span>
          </div>
        ) : null}

        <div className={styles.row}>
          <Input
            aria-label="Secret name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
            placeholder="STRIPE_API_KEY"
            style={{ flex: 1, fontFamily: "var(--font-mono)" }}
            value={name}
          />
          <Input
            aria-label="Secret value"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
            placeholder="value"
            style={{ flex: 1 }}
            type="password"
            value={value}
          />
          <Button disabled={!canAdd} onClick={add} size="sm" variant="accent">
            <Icon.Plus size={13} /> {replacing ? "Replace" : "Add"}
          </Button>
        </div>

        {error ? (
          <div className={styles.row}>
            <span
              className={styles.help}
              style={{ color: "var(--needs-input)" }}
            >
              {error}
            </span>
          </div>
        ) : null}
      </div>
    </section>
  );
};
